/**
 * S07/G16: Parcel Metadata State
 * In-memory state management for parcel metadata (displayed in Map tab)
 * Supports both single parcel and multi-parcel (merged) storage
 */

import type { ParcelRegion, PL2000Coordinate } from './types'

/** Subset of ParcelResult — metadata + optional PL-2000 boundary for map/building operations */
export interface ParcelMetadata {
  id: string
  area: number
  region: ParcelRegion
  address?: string
  /** PL-2000 boundary — stored for merged parcels (composite ID can't be re-fetched from ULDK) */
  boundaryPl2000?: PL2000Coordinate[]
}

let currentParcel: ParcelMetadata | null = null

/** Source parcels that were merged (when multi-parcel import was used) */
let sourceParcels: ParcelMetadata[] = []

/**
 * Set the current parcel metadata
 */
export function setParcel(parcel: ParcelMetadata | null): void {
  currentParcel = parcel
  if (!parcel) {
    sourceParcels = []
  }
}

/**
 * Get the current parcel metadata
 */
export function getParcel(): ParcelMetadata | null {
  return currentParcel
}

/**
 * Clear the current parcel metadata
 */
export function clearParcel(): void {
  currentParcel = null
  sourceParcels = []
}

/**
 * Check if parcel metadata is set
 */
export function hasParcel(): boolean {
  return currentParcel !== null
}

/**
 * Set the source parcels that were merged into the current parcel
 */
export function setParcels(parcels: ParcelMetadata[]): void {
  sourceParcels = parcels
}

/**
 * Get the source parcels (empty if single parcel import)
 */
export function getParcels(): ParcelMetadata[] {
  return sourceParcels
}
